import { styleForTone } from '@/shared/config/event.config';
import { useEventConfig } from '@/shared/config/event-config-context';
import { cn } from '@/shared/lib/cn';
import { formatCount } from '@/shared/lib/datetime';

interface GroupTabsProps {
  active: string;
  /** Total de registros do grupo ativo — só ele é conhecido sem requests extras. */
  activeCount: number | null;
  onChange: (group: string) => void;
}

/**
 * Abas de grupo em formato de segmented control.
 *
 * As abas são os `status` da parametrização, na ordem que ela define — nome, ordem
 * e cor (derivada do `code`) vêm de lá. Nada de lista fixa aqui.
 *
 * Semântica de `tablist` com navegação por setas: o painel é operado em
 * sequência por quem revisa muitas inscrições, e teclado importa.
 */
export function GroupTabs({ active, activeCount, onChange }: GroupTabsProps) {
  const { groups } = useEventConfig();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;

    event.preventDefault();
    const currentIndex = groups.findIndex(group => group.key === active);
    const next = groups[(currentIndex + delta + groups.length) % groups.length];
    if (next) onChange(next.key);
  };

  return (
    <div
      role="tablist"
      aria-label="Situação da inscrição"
      onKeyDown={handleKeyDown}
      className="inline-flex w-full gap-1 rounded-xl border border-line bg-surface-subtle p-1 sm:w-auto"
    >
      {groups.map(group => {
        const isActive = group.key === active;

        return (
          <button
            key={group.key}
            role="tab"
            type="button"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(group.key)}
            className={cn(
              'flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-[0.8125rem] font-medium whitespace-nowrap transition-colors duration-150 sm:flex-none',
              isActive ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink',
            )}
          >
            <span className={cn('size-1.5 shrink-0 rounded-full', styleForTone(group.tone).dotClassName)} aria-hidden="true" />
            {group.label}
            {isActive && activeCount !== null ? (
              <span className="tabular rounded-full bg-surface-subtle px-1.5 text-[0.6875rem] text-ink-muted">{formatCount(activeCount)}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
